/**
 * "Wordt verzonden" in de zaak (sessie 72, blok 1). De winkel staat in
 * `../verzendingen.ts`, de volger in `verzendVolger.ts`.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { v6Api } from "../api";
import { Icoon } from "../Icoon";
import { naTerugzetten } from "../naActie";
import {
  useVerzendingen,
  voegVerzendingToe,
  zetVerzending,
  type Verzending,
} from "../verzendingen";
import { htmlNaarTekst } from "./mailopmaak";
import { klokje } from "./tijd";
import "./verzending.css";

function eersteRegel(v: Verzending): string {
  if (v.onderwerp?.trim()) return v.onderwerp.trim();
  const tekst = htmlNaarTekst(v.tekst).trim().split("\n")[0] ?? "";
  if (!tekst) return "Bericht";
  return tekst.length > 80 ? `${tekst.slice(0, 79)}…` : tekst;
}

/**
 * De regels onder de berichten: wordt verzonden, verzonden of mislukt.
 * Sessie 75: een gepland bericht staat er ook na herladen of op een ander
 * apparaat (uit de wachtrij van de server), met Intrekken.
 */
export function VerzendRegels({ zaakId, gepland = false }: { zaakId: string; gepland?: boolean }) {
  const qc = useQueryClient();
  const lijst = useVerzendingen(zaakId);
  const [bezig, setBezig] = useState<string | null>(null);
  const heeftWacht = lijst.some((v) => v.stand === "wacht");
  useEffect(() => {
    if (!gepland || heeftWacht) return;
    let actief = true;
    v6Api
      .wachtrij(zaakId)
      .then((rijen) => {
        if (!actief) return;
        for (const rij of rijen) {
          if (rij.status !== "wacht") continue;
          voegVerzendingToe(qc, {
            wachtrijId: rij.id,
            zaakId,
            kanaal: rij.kanaal,
            tekst: "",
            onderwerp: rij.onderwerp,
            verstuurtOp: Date.parse(rij.verstuurt_op),
            gepland: true,
          });
        }
      })
      .catch(() => undefined);
    return () => {
      actief = false;
    };
  }, [gepland, heeftWacht, qc, zaakId]);
  const trekIn = async (v: Verzending) => {
    setBezig(v.wachtrijId);
    try {
      await v6Api.intrekken(v.wachtrijId);
      zetVerzending(qc, v.wachtrijId, { stand: "ingetrokken" });
      naTerugzetten(qc, zaakId);
    } finally {
      setBezig(null);
    }
  };
  const zichtbaar = lijst.filter((v) => v.stand !== "ingetrokken");
  if (zichtbaar.length === 0) return null;
  return (
    <div className="v6-verzendingen">
      {zichtbaar.map((v) => (
        <div
          key={v.wachtrijId}
          className={`v6-verzending ${v.stand}`}
          data-testid="v6-verzending"
          role={v.stand === "mislukt" ? "alert" : undefined}
          aria-live="polite"
        >
          <Icoon naam={v.stand === "mislukt" ? "x" : "stuur"} klein />
          <span className="wat">{eersteRegel(v)}</span>
          <span className="stand">
            {v.stand === "wacht"
              ? v.gepland
                ? `gepland, wordt verzonden om ${klokje(v.verstuurtOp)}`
                : "wordt verzonden"
              : v.stand === "verzonden"
                ? "verzonden"
                : `Versturen mislukt: ${v.reden ?? "onbekende fout"}. De tekst staat weer in het vak.`}
          </span>
          {v.stand === "wacht" && v.gepland ? (
            <button
              type="button"
              className="v6-knop intrek"
              disabled={bezig === v.wachtrijId}
              onClick={() => void trekIn(v)}
            >
              Intrekken
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}
